import type { ReactNode } from 'react'
import { TableHeaderCell, makeStyles } from '@fluentui/react-components'
import { ArrowSortDown20Regular, ArrowSortUp20Regular } from '@fluentui/react-icons'
import type { SortCriterion } from '../hooks/useColumnSort'
import { bayerColors } from '../theme'

const useStyles = makeStyles({
  header: {
    cursor: 'pointer',
    userSelect: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    ':hover': { color: bayerColors.navyMid },
  },
  icon: { opacity: 0.6, fontSize: '14px' },
  rank: {
    fontSize: '10px',
    fontWeight: 700,
    lineHeight: 1,
    color: bayerColors.navyMid,
    background: '#eef3f8',
    borderRadius: '8px',
    padding: '1px 4px',
    marginLeft: '1px',
  },
})

interface SortableHeaderProps<K extends string> {
  sortKey: K
  criteria: SortCriterion<K>[]
  onSort: (key: K, additive: boolean) => void
  children: ReactNode
  width?: string
}

/**
 * A sortable table header cell: click to sort, Shift-click to add a tiebreaker
 * column. Shows a direction arrow, plus a numbered rank badge when more than
 * one column is active. Shared by the demand/product/submission tables.
 */
export function SortableHeader<K extends string>({
  sortKey,
  criteria,
  onSort,
  children,
  width,
}: SortableHeaderProps<K>) {
  const styles = useStyles()
  const idx = criteria.findIndex((c) => c.key === sortKey)
  const crit = idx >= 0 ? criteria[idx] : undefined
  return (
    <TableHeaderCell style={width ? { width } : undefined}>
      <span
        className={styles.header}
        onClick={(e) => onSort(sortKey, e.shiftKey)}
        title="Click to sort · Shift-click to add a column"
        data-testid={`sort-header-${sortKey}`}
      >
        {children}
        {crit &&
          (crit.dir === 'asc' ? (
            <ArrowSortUp20Regular className={styles.icon} />
          ) : (
            <ArrowSortDown20Regular className={styles.icon} />
          ))}
        {crit && criteria.length > 1 && (
          <span className={styles.rank} data-testid={`sort-rank-${sortKey}`}>
            {idx + 1}
          </span>
        )}
      </span>
    </TableHeaderCell>
  )
}
